import { NotImplementedError } from "../../../../errors";
import { buildTrendBucketKeys, parseBidstreamTrendResponse } from "../../../../models/looker/modules/bidstream";
import { PromiseHandler } from "../../../../handlers";
import { defaultDateRange, formatDateRange } from "../shared/dateRange";
import { BidstreamTrendFetchFailed } from "./errors";
import { buildBidstreamTrendQuery } from "./query";
const DEFAULT_WINDOW_HOURS = 24;
/**
 * Win/Loss trend over time for the Bidstream drawer (SIGHT-2198). Split from
 * the Bidstream summary handler rather than branched inside it: the two return
 * different shapes and cache under different queries.
 */
class BidstreamTrend extends PromiseHandler {
    constructor(sdk) {
        super(sdk, "looker-bidstream-trend");
    }
    async findItems(filters) {
        const lineItemId = filters.where?.find((filter) => filter.field === "lineItemId")?.value;
        const granularityFilter = filters.where?.find((filter) => filter.field === "granularity")?.value;
        const windowHoursFilter = filters.where?.find((filter) => filter.field === "windowHours")?.value;
        const dealIdsFilter = filters.where?.find((filter) => filter.field === "dealIds")?.value;
        const forceRefresh = filters.where?.find((filter) => filter.field === "forceRefresh")?.value === true;
        if (typeof lineItemId !== "string" || !lineItemId) {
            throw new Error("lineItemId filter is required");
        }
        const granularity = granularityFilter === "day" ? "day" : "hour";
        const windowHours = typeof windowHoursFilter === "number" && windowHoursFilter > 0
            ? windowHoursFilter
            : DEFAULT_WINDOW_HOURS;
        const dealIds = Array.isArray(dealIdsFilter)
            ? dealIdsFilter.filter((id) => typeof id === "string")
            : [];
        // Absolute range, matching the summary query - a relative Looker
        // expression is evaluated at query time, which can drift from
        // buildTrendBucketKeys' floored "now" and drop edge rows.
        const dates = defaultDateRange(windowHours, granularity === "day" ? "day" : "hour");
        // buildTrendBucketKeys expects `now` inside the current bucket; dates[1]
        // sits exactly on its boundary, so back up 1ms to land inside the last
        // real bucket instead of the not-yet-started one.
        const bucketAnchor = new Date(dates[1].getTime() - 1);
        const queryFilters = {
            "explore_bidstream_base.flt_lineitem_id": lineItemId,
            "explore_bidstream_base.flt_analysis_interval": formatDateRange(dates),
            // Looker string filters treat a comma-separated value as an OR list.
            ...(dealIds.length > 0 && {
                "explore_bidstream_base.flt_deal_id": dealIds.join(",")
            })
        };
        const queryBody = buildBidstreamTrendQuery(granularity, queryFilters);
        const hashKey = await this.sdk.looker.query.hashQuery(queryBody);
        // Evicts this exact query's cache entry so the Refresh button always
        // hits the network instead of possibly replaying a stale cached result -
        // see the matching comment on Bidstream.findItems (./index.ts).
        if (forceRefresh) {
            this.cache.remove(hashKey);
        }
        return this.cache.promise(hashKey, () => this.sdk.looker.query
            .run(queryBody)
            .then(({ data, queryMeta }) => [
            {
                data: parseBidstreamTrendResponse(data, granularity, buildTrendBucketKeys(granularity, windowHours, bucketAnchor)),
                queryMeta
            }
        ])
            .catch((error) => {
            throw new BidstreamTrendFetchFailed(error);
        }));
    }
    async saveItem() {
        throw new NotImplementedError("saveItem");
    }
    async deleteItem() {
        throw new NotImplementedError("deleteItem");
    }
}
export default BidstreamTrend;
//# sourceMappingURL=trend.js.map